import React, {useCallback, useEffect, useState} from 'react';
import {DomEventListener} from '@wix/devzai-utils-dom';
import {assertNotNullable, referencesEqualityComparer} from '@wix/devzai-utils-common';
import {useLayoutBinding} from "./element-layout-binding-hooks";

export function useScrollTopBinding(
    scrollViewElementRef: React.RefObject<Element | Window> | 'window'
) {
    return useScrollValueBinding('vertical', scrollViewElementRef);
}

export function useScrollLeftBinding(
    scrollViewElementRef: React.RefObject<Element | Window> | 'window'
) {
    return useScrollValueBinding('vertical', scrollViewElementRef);
}

export function useScrollValueBinding(
    orientation: 'horizontal' | 'vertical',
    scrollViewRef: React.RefObject<Element | Window> | 'window'
) {
    const [scrollValue, setScrollValue] = useState<number>();

    useEffect(() => {

        let listeningTarget: DomEventListener.EventListeningTarget;
        let scrollViewElement: Element;
        if (scrollViewRef === 'window') {
            listeningTarget = window;
            scrollViewElement = document.documentElement;
        } else {
            const scrollView = assertNotNullable(scrollViewRef.current);

            if (scrollView instanceof Window || scrollView.matches('html')) {
                listeningTarget = window;
                scrollViewElement = document.documentElement;
            } else {
                listeningTarget = scrollViewElement = scrollView;
            }
        }

        const syncScrollValue = () => {
            setScrollValue(
                orientation === 'vertical' ? scrollViewElement.scrollTop : scrollViewElement.scrollLeft);
        };

        const domEventListener = new DomEventListener(listeningTarget, 'scroll', () => {
            syncScrollValue()
        }).activate();

        syncScrollValue();

        return () => {
            domEventListener.deactivate();
        };

    }, [scrollViewRef, orientation]);

    return scrollValue;
}

export function useScrollHeightBinding(elementRef: React.RefObject<Element | Window> | 'window', isEnabled = true) {
    return useScrollSizeBinding('vertical', elementRef, isEnabled)
}

export function useScrollWidthBinding(elementRef: React.RefObject<Element | Window> | 'window', isEnabled = true) {

    return useScrollSizeBinding('horizontal', elementRef, isEnabled)
}

export function useScrollSizeBinding(
    orientation: 'horizontal' | 'vertical',
    elementRef: React.RefObject<Element | Window> | 'window',
    isEnabled = true
) {

    const scrollSizeProp = orientation === 'horizontal' ? 'scrollWidth' : 'scrollHeight';

    return useLayoutBinding(
        useCallback(() => {

            if (elementRef === 'window' || elementRef.current instanceof Window) {
                return document.documentElement[scrollSizeProp]
            } else {
                return elementRef.current?.[scrollSizeProp] ?? undefined;
            }

        }, [elementRef]),
        referencesEqualityComparer,
        isEnabled
    )
}

export function useMaxScrollTopBinding(elementRef: React.RefObject<Element | Window> | 'window', isEnabled = true) {
    return useMaxScrollValueBinding('vertical', elementRef, isEnabled)
}

export function useMaxScrollLeftBinding(elementRef: React.RefObject<Element | Window> | 'window', isEnabled = true) {
    return useMaxScrollValueBinding('horizontal', elementRef, isEnabled)
}

export function useMaxScrollValueBinding(
    orientation: 'horizontal' | 'vertical',
    elementRef: React.RefObject<Element | Window> | 'window',
    isEnabled = true
) {

    const scrollSizeProp = orientation === 'horizontal' ? 'scrollWidth' : 'scrollHeight';
    const sizeProp = orientation === 'horizontal' ? 'clientWidth' : 'clientHeight';

    return useLayoutBinding(
        useCallback(() => {

            if (elementRef === 'window' || elementRef.current instanceof Window) {
                return document.documentElement[scrollSizeProp] - document.documentElement[sizeProp]
            } else {

                const element = elementRef.current;

                if (element) {
                    return element[scrollSizeProp] - element[sizeProp];
                } else {
                    return undefined;
                }
            }

        }, [elementRef]),
        referencesEqualityComparer,
        isEnabled
    )
}